/** Callers include user/project/filter scope in keys; this cache never authorizes a request. */
export function createAsyncResourceCache<T>(ttlMs: number, maxEntries = 50) {
  const entries = new Map<string, { value: T; expiresAt: number }>();
  const pending = new Map<string, Promise<T>>();
  const get = (key: string): T | undefined => {
    const entry = entries.get(key);
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) {
      entries.delete(key);
      return undefined;
    }
    return entry.value;
  };
  const load = (key: string, loader: () => Promise<T>): Promise<T> => {
    const cached = get(key);
    if (cached !== undefined) return Promise.resolve(cached);
    const existing = pending.get(key);
    if (existing) return existing;
    const request = Promise.resolve()
      .then(loader)
      .then(value => {
        if (entries.size >= maxEntries) {
          entries.delete(entries.keys().next().value!);
        }
        entries.set(key, { value, expiresAt: Date.now() + ttlMs });
        return value;
      })
      .finally(() => {
        if (pending.get(key) === request) pending.delete(key);
      });
    pending.set(key, request);
    return request;
  };
  return { get, load };
}
